<script>
    import Select from "svelte-select";
    import { slide } from 'svelte/transition'
    import DateSearcher from "./DateSearcher.svelte";
    import RadioButtons from "./RadioButtons.svelte";
    import CitySearcher from "./CitySearcher.svelte";
    import AreaSearcher from "./AreaSearcher.svelte";
    import { onMount } from "svelte";
    import Searchbar from "./SearchBar.svelte";
    import { subscriberTableFilter } from "../../stores/subscriberTableStore";
    import { addStringSort, removeFilterFromKeys } from "../../utils/sortFunction";
    import { settings } from "../../stores/settingsStore";
    import GroupSearcher from "./GroupSearcher.svelte";
    import { getGroup } from "../../services/groupService";
    import { getCityByCode } from "../../services/addressService";

    export let isPrints = false;
    let temporalFilter = "referenceYear";
    let spaceFilter = "residenceCountry";
    let statusFilter;
    let sortColumn;
    let sortDirection;
    let referenceYears = [];
    let isExpanded = true;
    let searchedGroup = null;
    let residenceCityCodeValue = null;

    const statusFilterOptions = [
        { value: "isOnlinePayment", label: "Pagamento" },
        { value: "isUGS", label: "Partecipazione UGS" },
        { value: "groupId", label: "Gruppo" },
        { value: "shopId", label: "Identificativo pagamento" },
    ];
    const temporalFilterOptions = [
        { value: "referenceYear", label: "Anno" },
        { value: "membershipCreatedAt", label: "Inserimento" },
        { value: "paidAt", label: "Attivazione" }
    ];
    const areaFilterOptions = [
        { value: "residenceCountry", label: "Area" },
        { value: "residenceRegion", label: "Regione" },
        { value: "residenceProvince", label: "Provincia" },
        { value: "residenceCityCode", label: "Comune" },
    ];
    const wasPrintedOptions = [
        { label: "Stampato", value: "true", id: "wasPrintedYes" },
        { label: "Non stampato", value: "false", id: "wasPrintedNo" },
    ];
    const paymentMethodOptions = [
        { label: "Bonifico Bancario", value: "false", id: "isOnlinePaymentFalse" },
        { label: "Pagamento Online", value: "true", id: "isOnlinePaymentTrue" },
    ];
    const isUGSOptions = [
        { label: "Partecipante", value: "true", id: "isUGSYes" },
        { label: "Non partecipante", value: "false", id: "isUGSNo" },
    ];
    const activeMembershipOptions = [
        { label: "Attivo", value: "true", id: "activeMembership" },
        { label: "Non attivo", value: "false", id: "inactiveMembership" },
    ];
    const foreignOptions = [
        { label: "Italia", value: "false", id: "italianSubscriber" },
        { label: "Estero", value: "true", id: "foreignSubscriber" },
    ];
    const sortOptions = [
        { value: "country", label: "Stato residenza" },
        { value: "region", label: "Regione" },
        { value: "province", label: "Provincia" },
        { value: "city", label: "Città" },
        { value: "createdAt", label: "Data iscrizione" },
        { value: "membership_createdAt", label: "Data inserimento" },
        { value: "membership_paidAt", label: "Data attivazione" }
    ];
    const directionOptions = [
        { value: "asc", label: "Crescente (A-Z)" },
        { value: "desc", label: "Decrescente (Z-A)" }
    ];

    function handleSort(sortColumn) {
        if (!sortDirection || !sortColumn) {
            return;
        }

        const newSorts = addStringSort($subscriberTableFilter?.sort, sortColumn.value, sortDirection.value);
        $subscriberTableFilter = {
            ...$subscriberTableFilter,
            sort: removeFilterFromKeys(newSorts, sortOptions.map((o) => o.value).filter(key => key !== sortColumn.value))
        };
    }

    function clearSort() {
        if (!sortColumn) {
            return;
        }

        $subscriberTableFilter = {
            ...$subscriberTableFilter,
            sort: removeFilterFromKeys($subscriberTableFilter?.sort, sortOptions.map((o) => o.value).filter(key => key === sortColumn.value))
        };

        sortColumn = undefined;
        sortDirection = undefined;
    }

    function generateRefYears () {
        const minYear = 2017;

        referenceYears = Array(Math.max(new Date().getFullYear(), $settings.subscriptionActiveYear) - 2017 + 1)
            .fill(null)
            .map((_, i) => minYear + i);
    }

    function expandFilters () {
        isExpanded = !isExpanded;
    }

    function generateStatusFilter() {
        statusFilterOptions.push({
            value: isPrints ? "wasPrinted" : "active",
            label: isPrints ? "Stampa" : "Attivazione"
        });
    }

    function updateFilters(key, newValue) {
        $subscriberTableFilter = { ...($subscriberTableFilter ?? {}), page: 1, [key]: newValue };
    }

    function selectFilters(keys, filters) {
        return Object.keys(filters).find((filterKey) => keys.includes(filterKey));
    }

    function cleanFilters(options) {
        for (const key of options.map((o) => o.value)) {
            delete $subscriberTableFilter[key];
        }

        $subscriberTableFilter = { ...$subscriberTableFilter };
    }

    onMount(async () => {
        const componentSortKeys = sortOptions.map((o) => o.value);
        const searchSectionSort = $subscriberTableFilter.sort?.split("-")?.find((sort) => {
            const [key, _] = sort.split(",");
            return componentSortKeys.includes(key);
        });

        if (!!searchSectionSort) {
            const [column, direction] = searchSectionSort.split(",");

            sortColumn = sortOptions.find(o => o.value === column);
            sortDirection = directionOptions.find(o => o.value === direction);
        }

        if (!!$subscriberTableFilter.groupId) {
            searchedGroup = (await getGroup($subscriberTableFilter.groupId)).data;
        }

        if (!!$subscriberTableFilter.residenceCityCode) {
            const city = (await getCityByCode($subscriberTableFilter.residenceCityCode)).data;
            residenceCityCodeValue = { value: city.code, label: `${city.name} - ${city.province}` };
        }

        generateStatusFilter();
        generateRefYears();

        temporalFilter = selectFilters(temporalFilterOptions.map((f) => f.value), $subscriberTableFilter);
        spaceFilter = selectFilters(areaFilterOptions.map((f) => f.value), $subscriberTableFilter);
        statusFilter = selectFilters(statusFilterOptions.map((f) => f.value), $subscriberTableFilter);
    });

    $: sortDirection && handleSort(sortColumn)
</script>

<div class="w-full flex flex-col gap-4 relative">
    <div class="flex flex-col gap-1">
        <div class="text-gray-500 flex flex-row justify-between">
            <div>Filtra per</div>

            <div class="text-lightBlue-500 cursor-pointer" on:click={() => expandFilters()}>
                {isExpanded ? "Riduci" : "Espandi"}
                <i class="fa pl-4  {isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}" />
            </div>
        </div>

        <div class="flex flex-col items-center gap-1 w-full lg:flex-row">
            <Searchbar
                placeholder="Filtra per nome dell'iscritto"
                hasLabel={false}
                value={$subscriberTableFilter.firstName}
                on:search={(e) => updateFilters("firstName", e.detail) }
            />

            <Searchbar
                placeholder="Filtra per cognome dell'iscritto"
                hasLabel={false}
                value={$subscriberTableFilter.lastName}
                on:search={(e) => updateFilters("lastName", e.detail) }
            />
        </div>

        <Searchbar
            placeholder="Filtra per email dell'iscritto"
            hasLabel={false}
            value={$subscriberTableFilter.email}
            on:search={(e) => updateFilters("email", e.detail) }
        />

        {#if isExpanded}
            <div class="flex flex-col items-center gap-1 w-full relative" in:slide>
                <div class="flex flex-col items-center gap-1 w-full lg:flex-row">
                    <div class="w-full lg:w-6/12">
                        <Select
                            items={temporalFilterOptions}
                            floatingConfig={{ placement: 'top-start' }}
                            id="timeFilter"
                            type= "text"
                            clearable={false}
                            containerStyles={`border: 1px solid #D8D4D4; max-height: 2rem !important; min-height: 2.75rem; border-radius: 0px`}
                            placeholder="Seleziona filtro temporale"
                            on:change={() => cleanFilters(temporalFilterOptions.filter(o => o.value !== "referenceYear"))}
                            bind:value={temporalFilter}
                            on:input={(e) => updateFilters("timeFilter", e.detail?.value)}
                        />
                    </div>

                    <div class="w-full lg:w-6/12">
                        {#if temporalFilter?.value === "referenceYear"}
                            <select
                                value={$subscriberTableFilter.referenceYear}
                                on:change={(e) => updateFilters("referenceYear", +e.target.value)}
                                class="w-full input-home no-shadow outline-none h-11 focus:outline-none"
                            >
                                {#each referenceYears as year}
                                    <option value={year}>{year}</option>
                                {/each}
                            </select>
                        {:else if temporalFilter?.value === "membershipCreatedAt"}
                            <div class="flex gap-1 h-11">
                                <DateSearcher
                                    containerClass="no-shadow"
                                    placeholder="Dal"
                                    bind:value={$subscriberTableFilter.membershipCreatedFrom}
                                />

                                <DateSearcher
                                    containerClass="no-shadow"
                                    placeholder="Al"
                                    bind:value={$subscriberTableFilter.membershipCreatedTo}
                                />
                            </div>
                        {:else}
                            <div class="flex gap-1 h-11 border">
                                <div class="w-full flex items-center">
                                    <DateSearcher
                                        containerClass="no-shadow"
                                        placeholder="Dal"
                                        bind:value={$subscriberTableFilter.paymentDateFrom}
                                    />
                                </div>
                                <div class="w-full flex items-center">
                                    <DateSearcher
                                        containerClass="no-shadow"
                                        placeholder="Al"
                                        bind:value={$subscriberTableFilter.paymentDateTo}
                                    />
                                </div>
                            </div>
                        {/if}
                    </div>
                </div>

                <div class="flex flex-col items-center gap-1 w-full lg:flex-row">
                    <div class="w-full lg:w-6/12">
                        <Select
                            items={areaFilterOptions}
                            floatingConfig={{placement: 'top-start'}}
                            type= "text"
                            containerStyles={`border: 1px solid #D8D4D4; max-height: 2rem !important; min-height: 2.75rem; border-radius: 0px`}
                            clearable={false}
                            placeholder="Seleziona filtro spaziale"
                            on:change={() => { residenceCityCodeValue = null; cleanFilters(areaFilterOptions); }}
                            bind:value={spaceFilter}
                        />
                    </div>

                    <div class="w-full lg:w-6/12">
                        {#if !spaceFilter}
                            <div class="flex items-center h-11 input-box-disabled pl-4">
                                Seleziona un filtro
                            </div>
                        {:else if spaceFilter.value === "residenceCountry"}
                            <RadioButtons
                                externalContainerClass="gap-1"
                                containerClass="no-shadow"
                                options={foreignOptions}
                                isNullable={true}
                                bind:inputValue={$subscriberTableFilter.residenceForeign}
                            />
                        {:else if spaceFilter.value === "residenceRegion"}
                            <div class="flex gap-1 h-11">
                                <AreaSearcher
                                    bind:valueName={$subscriberTableFilter.residenceRegion}
                                    subject="region"
                                    placeholder="Seleziona una regione"
                                    clearable="true"
                                />
                            </div>
                        {:else if spaceFilter.value === "residenceCityCode"}
                            <CitySearcher
                                value={residenceCityCodeValue}
                                handleChange={(e) => {
                                    residenceCityCodeValue = { value: e.detail.value.code, label: e.detail.label };
                                    updateFilters("residenceCityCode", e.detail.value.code);
                                }}
                                isSearchBar={true}
                            />
                        {:else}
                            <AreaSearcher
                                bind:valueName={$subscriberTableFilter.residenceProvince}
                                subject="province"
                                placeholder="Seleziona una provincia"
                                clearable="true"
                            />
                        {/if}
                    </div>
                </div>

                <div class="flex flex-col items-center gap-1 w-full lg:flex-row">
                    <div class="w-full lg:w-6/12">
                        <Select
                            items={statusFilterOptions}
                            floatingConfig={{placement: 'top-start'}}
                            id="statusFilter"
                            clearable="{false}"
                            type= "text"
                            containerStyles={`border: 1px solid #D8D4D4; max-height: 2rem !important; min-height: 2.75rem; border-radius: 0px`}
                            placeholder="Seleziona filtro per le iscrizioni"
                            on:change={() => cleanFilters(statusFilterOptions)}
                            bind:value={statusFilter}
                        />
                    </div>

                    <div class="w-full lg:w-6/12">
                        {#if !statusFilter}
                            <div class="flex items-center h-11 input-box-disabled pl-4">
                                Seleziona un filtro
                            </div>
                        {:else if statusFilter.value === "isOnlinePayment"}
                            <RadioButtons
                                externalContainerClass="gap-1"
                                containerClass="no-shadow"
                                options={paymentMethodOptions}
                                isNullable={true}
                                inputValue={$subscriberTableFilter.isOnlinePayment}
                                on:input={(e) => updateFilters("isOnlinePayment", e.detail.value)}
                            />
                            {:else if statusFilter.value === "wasPrinted"}
                                <RadioButtons
                                    externalContainerClass="gap-1"
                                    containerClass="no-shadow"
                                    options={wasPrintedOptions}
                                    isNullable={true}
                                    inputValue={$subscriberTableFilter.wasPrinted}
                                    on:input={(e) => updateFilters("wasPrinted", e.detail.value)}
                                />
                            {:else if statusFilter.value === "isUGS"}
                                <RadioButtons
                                    externalContainerClass="gap-1"
                                    containerClass="no-shadow"
                                    options={isUGSOptions}
                                    isNullable={true}
                                    inputValue={$subscriberTableFilter.isUGS}
                                    on:input={(e) => updateFilters("isUGS", e.detail.value)}
                                />
                            {:else if statusFilter.value === 'active'}
                                <RadioButtons
                                    externalContainerClass="gap-1"
                                    containerClass="no-shadow"
                                    options={activeMembershipOptions}
                                    isNullable={true}
                                    inputValue={$subscriberTableFilter.active}
                                    on:input={(e) => updateFilters("active", e.detail.value)}
                                />
                            {:else if statusFilter.value === 'shopId'}
                                <Searchbar
                                    placeholder="Filtra per identificativo pagamento"
                                    hasLabel={false}
                                    value={$subscriberTableFilter.shopId}
                                    on:search={(e) => updateFilters("shopId", e.detail) }
                                />
                            {:else}
                                <GroupSearcher
                                    isUserBased={true}
                                    isForm="{false}"
                                    value="{searchedGroup?.name}"
                                    on:change={(e) => updateFilters("groupId", e.detail.value)}
                                    on:clear={() => cleanFilters(statusFilterOptions)}
                                />
                        {/if}
                    </div>
                </div>
            </div>
        {/if}
    </div>

    <div>
        <div class="text-gray-500">Ordina per</div>

        <div class="flex flex-col items-center gap-1 w-full lg:flex-row">
            <div class="w-full lg:w-6/12">
                <Select
                    items={sortOptions}
                    floatingConfig={ {placement: 'top-start'}}
                    id="timeOrderBy"
                    type= "text"
                    containerStyles={`border: 1px solid #D8D4D4; max-height: 2rem !important; min-height: 2.75rem; border-radius: 0px`}
                    placeholder="Selziona un ordinamento"
                    on:change={() => handleSort(sortColumn)}
                    on:clear={() => clearSort()}
                    bind:value={sortColumn}
                />
            </div>

            <div class="w-full lg:w-6/12">
                <Select
                    items={directionOptions}
                    floatingConfig={{placement: 'top-start'}}
                    clearable={false}
                    placeholder="Seleziona un ordinamento"
                    disabled ={!sortColumn}
                    containerStyles={`border: 1px solid #D8D4D4; max-height: 2rem !important; min-height: 2.75rem; border-radius: 0px`}
                    bind:value={sortDirection}
                />
            </div>
        </div>
    </div>
</div>